import React from 'react'
import TemplatizedTextField, {
    type TemplatizedTextFieldProps,
} from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'
import classNames from 'classnames'
import { EMPTY_MACRO_SENTINEL } from './media-field-sources'
import './styles/dynamic-macro-field.scss'

export interface DynamicMacroFieldProps {
    value?: string | null
    onChange: (value: string) => void
    disabled?: boolean
    placeholder?: string
    className?: string
    /** Header label for the Liquid template editor modal (e.g. "Image macro"). */
    fieldName?: React.ReactNode
    /** Macro-picker config — typed against TemplatizedTextField so no casts are needed at call sites. */
    macroTypes?: TemplatizedTextFieldProps['macroTypes']
    customMacroOptions?: TemplatizedTextFieldProps['customMacroOptions']
    secretsMacroOptions?: TemplatizedTextFieldProps['secretsMacroOptions']
    validMacroOptionTypes?: TemplatizedTextFieldProps['validMacroOptionTypes']
}

/**
 * The "Dynamic Macro" source body for a media (icon) field: a dynamic placeholder tile beside the
 * reused macro picker (free-type or select).
 *
 * The value may be a macro, a plain URL, or a URL with an embedded macro; we don't verify it resolves
 * to an image. Presentational/controlled — the parent owns the value and persists it inline in
 * `icon_url`. The `EMPTY_MACRO_SENTINEL` (macro selected but nothing entered) renders as a blank input.
 */
const DynamicMacroField = (props: DynamicMacroFieldProps) => {
    const displayValue = props.value === EMPTY_MACRO_SENTINEL ? '' : props.value ?? ''

    return (
        <div className={classNames('dynamic-macro-field', props.className)}>
            <div className="dynamic-macro-field__tile" aria-hidden="true">
                <span className="dynamic-macro-field__glyph">{'{ }'}</span>
                <span className="dynamic-macro-field__tile-label">DYNAMIC</span>
            </div>

            <div className="dynamic-macro-field__main">
                <TemplatizedTextField
                    noStyle={true}
                    hideEmojis={true}
                    autoComplete="off"
                    expandable={true}
                    expandFieldName={props.fieldName ?? 'Macro'}
                    inputClassName="dynamic-macro-field__input"
                    placeholder={props.placeholder ?? 'Select or enter a macro'}
                    value={displayValue}
                    onChange={props.onChange}
                    disabled={props.disabled}
                    macroTypes={props.macroTypes}
                    customMacroOptions={props.customMacroOptions}
                    secretsMacroOptions={props.secretsMacroOptions}
                    validMacroOptionTypes={props.validMacroOptionTypes}
                />
            </div>
        </div>
    )
}

export default DynamicMacroField
